import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationIcon,
  AccountBalance as AccountIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/LegalLayout";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [timeframe, setTimeframe] = useState("quarterly");

  // Sample Analytics Data
  const cashFlowData = {
    labels: ["Q1", "Q2", "Q3", "Q4"],
    datasets: [
      {
        label: "Inflow",
        data: [120000, 145000, 135000, 160000],
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
      },
      {
        label: "Outflow",
        data: [100000, 110000, 105000, 125000],
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.2)",
      },
    ],
  };

  const clientSegmentationData = {
    labels: ["Small Business", "Startups", "Enterprise", "Freelancers"],
    datasets: [
      {
        data: [35, 25, 20, 20],
        backgroundColor: [
          "rgba(255, 99, 132, 0.8)",
          "rgba(54, 162, 235, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(75, 192, 192, 0.8)",
        ],
      },
    ],
  };

  const profitabilityAnalysisData = {
    labels: ["Product A", "Product B", "Product C", "Product D"],
    datasets: [
      {
        label: "Profit Margin %",
        data: [22, 35, 18, 25],
        backgroundColor: [
          "rgba(54, 162, 235, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(75, 192, 192, 0.8)",
          "rgba(153, 102, 255, 0.8)",
        ],
      },
    ],
  };

  const predictionData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Actual Revenue",
        data: [65000, 59000, 80000, 81000, 56000, 95000],
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
      {
        label: "Predicted Revenue",
        data: [67000, 62000, 82000, 83000, 58000, 97000],
        borderColor: "rgb(255, 99, 132)",
        borderDash: [5, 5],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
      },
    },
  };

  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Grid container spacing={3}>
            {/* Cash Flow Analysis */}
            <Grid item xs={12} md={8}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6">Cash Flow Analysis</Typography>
                <Bar
                  data={cashFlowData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        text: "Quarterly Cash Flow",
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>

            {/* Key Metrics */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
                <Typography variant="h6">Key Metrics</Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Chip
                    icon={<MonetizationIcon />}
                    label="Total Revenue: $560,000"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<TrendingUpIcon />}
                    label="Growth Rate: 12.5%"
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AccountIcon />}
                    label="Operating Margin: 25%"
                    color="secondary"
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            {/* Client Segmentation */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6">Client Segmentation</Typography>
                <Pie
                  data={clientSegmentationData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        text: "Client Type Distribution",
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>

            {/* Profitability Analysis */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6">Profitability Analysis</Typography>
                <Bar
                  data={profitabilityAnalysisData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        text: "Product Profit Margins",
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            {/* Predictive Analytics */}
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6">
                  Predictive Revenue Forecast
                </Typography>
                <Line
                  data={predictionData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      title: {
                        ...chartOptions.plugins.title,
                        text: "Actual vs Predicted Revenue",
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">
            <AnalyticsIcon sx={{ mr: 2, verticalAlign: "middle" }} />
            Advanced Analytics Dashboard
          </Typography>

          <FormControl variant="outlined" sx={{ minWidth: 200 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              label="Timeframe"
            >
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            centered
          >
            <Tab label="Financial Overview" />
            <Tab label="Client & Profitability" />
            <Tab label="Predictive Analytics" />
          </Tabs>
        </Box>

        {renderAnalyticsContent()}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AnalyticsIcon />}
          >
            Generate Full Report
          </Button>
          <Button variant="outlined" color="secondary">
            Export Data
          </Button>
        </Box>
      </Container>
    </Layout>
  );
};

export default AnalyticsDashboard;
